import { type TableInformation } from '../../../config/flavourConfig';
import type { CoverableDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/quote';
import type {
    YoungAccidentDTO,
    AdultAccidentDTO,
    ChildAccidentDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/accident';
import { type TravelDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/tra';
import { type SingleTripCancellationDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/stc';
import { type SingleTripTravelDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/stt';
import { type PrivateContentDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/content';
import { type CarDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/mvp';
import ViewHippoInformationModal from '../../../shared_components/ViewHippoInformationModal/ViewHippoInformationModal';
import { getTermValue } from '../../../utils/termsUtil';
import { type AgricultureContentDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/ctp';
import { AdjustItemKeys } from '../../AdjustDetailsPage/config/adjustDetails';

export const getImportantInformationCoverableDetailsConfig = (
    coverable: CoverableDTO,
    getHippoText: (resourceKey: string) => string
): TableInformation | undefined => {
    /* New item in below configs should have it own key,
     please check if that new item is existed in configs in getCoverableAdjustDetailsConfig(adjustDetails.ts)
     If yes then the existed item's key need to be reuse for the new item */
    switch (coverable.flavourName) {
        case 'Ulykkesforsikring':
            return getAdultAccidentImportantInformationConfig(coverable);
        case 'Ulykkesforsikring til unge':
            return getYoungAccidentImportantInformationConfig(coverable);
        case 'Børneforsikring':
            return getChildAccidentImportantInformationConfig(coverable);
        case 'Rejseforsikring':
        case 'Rejseforsikring til enkeltperson':
            return getTravelImportantInformationConfig(coverable);
        case 'Afbestillingsforsikring til enkeltrejse':
            return getSingleTripCancellationImportantInformationConfig(coverable);
        case 'Enkeltrejseforsikring':
            return getSingleTripTravelImportantInformationConfig(coverable);
        case 'Indboforsikring':
            return getPrivateContentImportantInformationConfig(coverable);
        case 'Privat løsøre- og ansvarsforsikring':
            return getAgricultureContentImportantInformationConfig(coverable);
        case 'Bilforsikring':
            return getCarImportantInformationConfig(coverable as CarDTO, getHippoText);
        default:
            return undefined;
    }
};

const getAdultAccidentImportantInformationConfig = (
    coverable: AdultAccidentDTO
): TableInformation => {
    return {
        title: '',
        rowsInformation: [
            {
                key: AdjustItemKeys.ACC_ADULT_INJURY_TOOTH_DAMAGE_SUM_INSURED_TERM,
                name: 'Forsikringssum ved varig mén',
                isAdjustableItem: true,
                fieldValue: `${getTermValue(
                    coverable,
                    'ACCPermanentInjuryToothDamageCov',
                    'ACCPermanentInjuryToothDamageCovSumInsured'
                )} kr.`
            },
            {
                key: AdjustItemKeys.ACC_ADULT_INJURY_TOOTH_DAMAGE_DEGREE_TERM,
                name: 'Méngrad',
                fieldValue: `${getTermValue(
                    coverable,
                    'ACCPermanentInjuryToothDamageCov',
                    'ACCPermanentInjuryToothDamageCovCovPIDegree'
                )}%`,
                labelType: 'modalLink',
                isAdjustableItem: true,
                modalInformation: {
                    modalType: ViewHippoInformationModal,
                    modalProps: {
                        title: 'DSS-ACC-HIGHLIGHTED-TEXT-MODAL-MENGRAD-TITLE',
                        description:
                            getTermValue(
                                coverable,
                                'ACCPermanentInjuryToothDamageCov',
                                'ACCPermanentInjuryToothDamageCovCovPIDegree'
                            ) === '5'
                                ? 'DSS-ACC-HIGHLIGHTED-TEXT-MODAL-MENGRAD-FIVE-PERCENT'
                                : 'DSS-ACC-HIGHLIGHTED-TEXT-MODAL-MENGRAD-EIGHT-PERCENT'
                    }
                }
            },
            {
                key: AdjustItemKeys.ACC_ADULT_FULLTIME_LEISURE,
                name: 'Tidsrum',
                isAdjustableItem: true,
                fieldValue: coverable.fullTimeOrLeisure,
                labelType: 'modalLink',
                modalInformation: {
                    modalType: ViewHippoInformationModal,
                    modalProps: {
                        title: 'DSS-ACC-HIGHLIGHTED-TEXT-MODAL-FULLTIMEORLEISURE-TITLE',
                        description: 'DSS-ACC-HIGHLIGHTED-TEXT-MODAL-FULLTIMEORLEISURE'
                    }
                }
            },
            {
                key: AdjustItemKeys.ACC_ADULT_EMPLOYMENT,
                name: 'Beskæftigelse',
                isAdjustableItem: true,
                fieldValue: coverable.employment,
                hidden: !coverable.employment
            }
        ]
    };
};

const getYoungAccidentImportantInformationConfig = (
    coverable: YoungAccidentDTO
): TableInformation => {
    return {
        title: '',
        rowsInformation: [
            {
                key: AdjustItemKeys.ACC_YOUNG_INJURY_TOOTH_DAMAGE_SUM_INSURED_TERM,
                name: 'Forsikringssum ved varig mén',
                fieldValue: `${getTermValue(
                    coverable,
                    'ACCPermanentInjuryToothDamageCov',
                    'ACCPermanentInjuryToothDamageCovSumInsured'
                )} kr.`
            },
            {
                key: AdjustItemKeys.ACC_YOUNG_INJURY_TOOTH_DAMAGE_DEGREE_TERM,
                name: 'Méngrad',
                fieldValue: `${getTermValue(
                    coverable,
                    'ACCPermanentInjuryToothDamageCov',
                    'ACCPermanentInjuryToothDamageCovCovPIDegree'
                )}%`,
                labelType: 'modalLink',
                modalInformation: {
                    modalType: ViewHippoInformationModal,
                    modalProps: {
                        title: 'DSS-ACC-HIGHLIGHTED-TEXT-MODAL-MENGRAD-TITLE',
                        description:
                            getTermValue(
                                coverable,
                                'ACCPermanentInjuryToothDamageCov',
                                'ACCPermanentInjuryToothDamageCovCovPIDegree'
                            ) === '5'
                                ? 'DSS-ACC-HIGHLIGHTED-TEXT-MODAL-MENGRAD-FIVE-PERCENT'
                                : 'DSS-ACC-HIGHLIGHTED-TEXT-MODAL-MENGRAD-EIGHT-PERCENT'
                    }
                }
            },
            {
                key: AdjustItemKeys.ACC_YOUNG_FULLTIME_LEISURE,
                name: 'Tidsrum',
                fieldValue: coverable.fullTimeOrLeisure,
                labelType: 'modalLink',
                modalInformation: {
                    modalType: ViewHippoInformationModal,
                    modalProps: {
                        title:
                            coverable.fullTimeOrLeisure === 'Heltid'
                                ? 'DSS-ACC-HIGHLIGHTED-TEXT-MODAL-HELTIDSDÆNING-TITLE'
                                : 'DSS-ACC-HIGHLIGHTED-TEXT-MODAL-FRITIDSDÆKNING-TITLE',
                        description:
                            coverable.fullTimeOrLeisure === 'Heltid'
                                ? 'DSS-ACC-HIGHLIGHTED-TEXT-MODAL-HELTIDSDÆNING'
                                : 'DSS-ACC-HIGHLIGHTED-TEXT-MODAL-FRITIDSDÆKNING'
                    }
                }
            }
        ]
    };
};

const getChildAccidentImportantInformationConfig = (
    coverable: ChildAccidentDTO
): TableInformation => {
    return {
        title: '',
        rowsInformation: [
            {
                key: AdjustItemKeys.ACC_CHILD_INJURY_TOOTH_DAMAGE_SUM_INSURED_TERM,
                name: 'Forsikringssum',
                fieldValue: `${getTermValue(
                    coverable,
                    'ACCChildPermInjuryAccidentCov',
                    'ACCChildPermInjAccCovSumInsured'
                )} kr.`
            },
            {
                key: AdjustItemKeys.ACC_CHILD_INJURY_TOOTH_DAMAGE_DEGREE_TERM,
                name: 'Méngrad',
                fieldValue: `${getTermValue(
                    coverable,
                    'ACCChildPermInjuryAccidentCov',
                    'ACCChildPermInjAccCovPermInjDegree'
                )}%`,
                labelType: 'modalLink',
                modalInformation: {
                    modalType: ViewHippoInformationModal,
                    modalProps: {
                        title: 'DSS-ACC-HIGHLIGHTED-TEXT-MODAL-MENGRAD-TITLE',
                        description: 'DSS-ACC-CHILD-HIGHLIGHTED-TEXT-MODAL-MENGRAD'
                    }
                }
            },
            {
                key: AdjustItemKeys.ACC_CHILD_FULLTIME_LEISURE,
                name: 'Tidsrum',
                fieldValue: coverable.fullTimeOrLeisure,
                labelType: 'modalLink',
                modalInformation: {
                    modalType: ViewHippoInformationModal,
                    modalProps: {
                        title: 'DSS-ACC-HIGHLIGHTED-TEXT-MODAL-HELTIDSDÆNING-TITLE',
                        description: 'DSS-ACC-HIGHLIGHTED-TEXT-MODAL-HELTIDSDÆNING'
                    }
                }
            }
        ]
    };
};

const getTravelImportantInformationConfig = (coverable: TravelDTO): TableInformation => {
    return {
        title: '',
        rowsInformation: [
            {
                key: AdjustItemKeys.TRA_AREA_DESTINATION,
                name: 'Forsikringen gælder rejser i',
                fieldValue: coverable.areaDestination
            }
        ]
    };
};

const getSingleTripCancellationImportantInformationConfig = (
    coverable: SingleTripCancellationDTO
): TableInformation => {
    return {
        title: '',
        rowsInformation: [
            {
                key: AdjustItemKeys.STC_NUMBER_INSURED_PERSONS,
                name: 'Personer, som er dækket ',
                fieldValue: coverable.numberOfInsuredPersons?.toString()
            },
            {
                key: AdjustItemKeys.STC_AREA_DESTINATION,
                name: 'Forsikringen gælder rejser i ',
                fieldValue: coverable.areaDestination
            },
            {
                key: AdjustItemKeys.STC_DEPARTURE_DATE,
                name: 'Rejsen starter',
                fieldValue: coverable.departureDate
            },
            {
                key: AdjustItemKeys.STC_TRAVEL_PRICE,
                name: 'Rejsens pris',
                fieldValue: coverable.travelPrice
            }
        ]
    };
};

const getSingleTripTravelImportantInformationConfig = (
    coverable: SingleTripTravelDTO
): TableInformation => {
    return {
        title: '',
        rowsInformation: [
            {
                key: AdjustItemKeys.STT_AREA_DESTINATION,
                name: 'Forsikringen gælder rejser i',
                fieldValue: coverable.areaDestination
            },
            {
                key: AdjustItemKeys.STT_DEPARTURE_DATE,
                name: 'Rejsen starter',
                fieldValue: coverable.departureDate
            },
            {
                key: AdjustItemKeys.STT_RETURN_DATE,
                name: 'Rejsen slutter',
                fieldValue: coverable.returnDate
            }
        ]
    };
};

const getCarImportantInformationConfig = (
    coverable: CarDTO,
    getHippoText: (resourceKey: string) => string
): TableInformation => {
    return {
        title: '',
        rowsInformation: [
            {
                key: AdjustItemKeys.MVP_CAR_YEARLY_MILEAGE,
                name: getHippoText('DSS-EXPANDED-SHOPPING-BASKET-MOST-IMPORTANT-DETAILS-SUBHEADER'),
                fieldValue: `${coverable.yearlyMileage} km`,
                isAdjustableItem: true
            }
        ]
    };
};

const getPrivateContentImportantInformationConfig = (
    coverable: PrivateContentDTO
): TableInformation => {
    return {
        title: '',
        rowsInformation: [
            {
                key: AdjustItemKeys.PRC_PRIVATE_CONTENT_NONE_COHOUSING_NUMBER_IN_HOUSEHOLD,
                name: 'Antal beboere tilmeldt din folkeregisteradresse',
                fieldValue: coverable.numberInHousehold?.toString(),
                hidden: coverable.hasCohousing
            },
            {
                key: AdjustItemKeys.PRC_PRIVATE_CONTENT_COHOUSING_NUMBER_IN_HOUSEHOLD,
                name: 'Antal beboere med folkeregisteradresse i bofællesskabet',
                fieldValue: coverable.numberInHousehold?.toString(),
                hidden: !coverable.hasCohousing
            }
        ]
    };
};

const getAgricultureContentImportantInformationConfig = (
    coverable: AgricultureContentDTO
): TableInformation => {
    return {
        title: '',
        rowsInformation: [
            {
                key: AdjustItemKeys.CTP_NUMBER_PERSON_IN_HOUSEHOLD,
                name: 'Antal beboere tilmeldt din folkeregisteradresse',
                fieldValue: coverable.numberPersonsInHousehold?.toString()
            },
            {
                key: AdjustItemKeys.CTP_HOURSE_NUMBER,
                name: 'Antal heste',
                fieldValue: coverable.numberOfHorses?.toString(),
                hidden: coverable.hasInsuredAnimals === false
            },
            {
                key: AdjustItemKeys.CTP_DOG_NUMBER,
                name: 'Antal hunde',
                fieldValue: coverable.numberOfDogs?.toString(),
                hidden: coverable.hasInsuredAnimals === false
            },
            {
                key: AdjustItemKeys.CTP_HAS_MACHINERY,
                name: 'Maskiner',
                fieldValue: coverable.hasMachinery
            }
        ]
    };
};
